import { z } from 'zod';
/**
 * LOOP theme.json — v1.0.0 공개 계약.
 *
 * 이 스키마는 OBS 위젯, 대시보드, API가 모두 공유하는 단일 소스입니다.
 * 파괴적 변경(키 제거/개명)은 major version bump와 ADR을 동반해야 합니다.
 * 추가(비파괴)는 패치/마이너로 허용.
 *
 * 참조: docs/DESIGN.md §6
 */
/* ------------------------------------------------------------------ */
/* Primitives                                                          */
/* ------------------------------------------------------------------ */
/** 6-digit hex, rgb/rgba, 'transparent' 허용. 보다 엄격한 파서로 대체 가능. */
const colorSchema = z
    .string()
    .refine((v) => v === 'transparent' ||
    /^#([0-9a-fA-F]{3,8})$/.test(v) ||
    /^rgba?\([\d.,%\s/-]+\)$/.test(v), { message: 'color는 hex, rgb/rgba, 또는 "transparent" 여야 합니다' });
const pxSchema = z.number().finite().nonnegative();
/* ------------------------------------------------------------------ */
/* A. Typography                                                       */
/* ------------------------------------------------------------------ */
export const typographySchema = z.object({
    fontFamily: z.object({
        text: z.string().min(1),
        number: z.string().min(1),
    }),
    fontSize: z.object({
        sm: pxSchema,
        base: pxSchema,
        lg: pxSchema,
    }),
    fontWeight: z.object({
        regular: z.number().int().min(100).max(900),
        bold: z.number().int().min(100).max(900),
    }),
    letterSpacing: z.number(),
    webfontUrl: z.string().url().nullable(),
});
/* ------------------------------------------------------------------ */
/* B. Color                                                            */
/* ------------------------------------------------------------------ */
export const colorTokensSchema = z.object({
    background: colorSchema,
    accent: colorSchema,
    text: colorSchema,
    stroke: colorSchema,
    glow: colorSchema.nullable(),
    tiers: z.object({
        small: colorSchema,
        medium: colorSchema,
        large: colorSchema,
    }),
});
/* ------------------------------------------------------------------ */
/* C. Shape & Space                                                    */
/* ------------------------------------------------------------------ */
export const shapeSchema = z.object({
    borderRadius: pxSchema,
    borderWidth: pxSchema,
    padding: z.object({
        toast: pxSchema,
        card: pxSchema,
    }),
    gap: pxSchema,
    /** CSS box-shadow 문자열. 빈 문자열 허용 */
    elevation: z.string(),
});
/* ------------------------------------------------------------------ */
/* D. Motion Preset (string enum only, v1 contract)                    */
/* ------------------------------------------------------------------ */
export const MOTION_PRESETS = ['snap', 'float', 'pulse', 'slide', 'bounce'];
export const motionPresetSchema = z.enum(MOTION_PRESETS);
/* ------------------------------------------------------------------ */
/* E. Sound Preset (string enum only, v1 contract)                     */
/* ------------------------------------------------------------------ */
export const SOUND_PRESETS = ['classic', 'minimal', 'arcade', 'silent'];
export const soundPresetSchema = z.enum(SOUND_PRESETS);
/* ------------------------------------------------------------------ */
/* Tiers                                                               */
/* ------------------------------------------------------------------ */
export const tiersSchema = z
    .object({
    /** [t1Max, t2Max]. t1Max 이하는 small, t1Max < x ≤ t2Max 는 medium, 초과는 large. */
    thresholds: z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()]),
    labels: z.tuple([z.literal('small'), z.literal('medium'), z.literal('large')]),
})
    .refine((t) => t.thresholds[0] < t.thresholds[1], {
    message: 'thresholds는 오름차순이어야 합니다',
});
/* ------------------------------------------------------------------ */
/* Root Theme                                                          */
/* ------------------------------------------------------------------ */
export const THEME_SCHEMA_VERSION = '1.0.0';
export const themeSchema = z.object({
    schemaVersion: z.literal(THEME_SCHEMA_VERSION),
    name: z.string().min(1).max(64),
    typography: typographySchema,
    color: colorTokensSchema,
    shape: shapeSchema,
    motionPreset: motionPresetSchema,
    soundPreset: soundPresetSchema,
    tiers: tiersSchema,
});
/* ------------------------------------------------------------------ */
/* Migration                                                           */
/* ------------------------------------------------------------------ */
/**
 * 저장된 theme JSON이 구버전일 경우 현재 버전으로 승급합니다.
 * v1.0.0에서는 자기 자신이 최신이므로 identity. 추가 버전이 생기면
 * 여기에 step-wise migration을 추가하세요.
 */
export function migrateTheme(raw) {
    if (typeof raw !== 'object' || raw === null)
        return raw;
    const obj = raw;
    const v = obj.schemaVersion;
    if (typeof v !== 'string')
        return raw;
    // 현재는 v1.0.0만 존재. 미래: switch/case로 단계별 승급.
    return raw;
}
/**
 * 테마를 파싱합니다. 실패 시 fallback으로 폴백하고 에러 목록을 반환합니다.
 * 위젯·대시보드·API가 모두 이 함수를 호출해야 crash 내성이 보장됩니다.
 */
export function parseTheme(raw, fallback) {
    const migrated = migrateTheme(raw);
    const result = themeSchema.safeParse(migrated);
    if (result.success) {
        return { ok: true, theme: result.data };
    }
    const errors = result.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`);
    return { ok: false, theme: fallback, errors };
}
/**
 * 기부 금액 → 티어 라벨. 위젯 상태 5/6/7 선택에 사용.
 */
export function tierForAmount(amount, tiers) {
    const [t1, t2] = tiers.thresholds;
    if (amount <= t1)
        return 'small';
    if (amount <= t2)
        return 'medium';
    return 'large';
}
//# sourceMappingURL=theme.js.map